import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import * as moment from "moment";
import Swal from "sweetalert2";
import {
	ExtendedRetrofitTestType,
	RetrofitService,
	RetrofitTestType,
} from "../../../../services/retrofit.service";
import { MetadataComponent } from "../../dialog/metadata/metadata.component";
import { ResourcesDownloadComponent } from "../../dialog/resources-download/resources-download.component";
import { FinalPdfReportComponent } from "../../final-pdf-report/final-pdf-report.component";
import { ExportToCsv, Options } from "export-to-csv";

type FinalResult = "Pass" | "Fail";

interface FileRef {
	file_name: string;
}

interface LoadMeta {
	final_result?: FinalResult;
	file_name?: string;
	test_date?: string;
	vehicle_number?: string;
}

interface MetaData {
	preload: LoadMeta;
	postload: Omit<LoadMeta, "test_date" | "vehicle_number">;
	fastcharge: Pick<LoadMeta, "final_result">;
	onroad: Pick<LoadMeta, "final_result" | "test_date">;
	fastchargeCsv: FileRef[];
	onroadCsv: FileRef[];
	fastchargePdf: FileRef;
	onroadPdf: FileRef;
}

interface DashboardDataRes {
	test_id: string;
	test_stage: string;
	test_status: FinalResult;
	metaData: MetaData;
}

export interface DashboardDataApiRes {
	success: boolean;
	message: string;
	data: DashboardDataRes[];
}

interface EolTableData {
	"S.No": number;
	"Test ID": string;
	"Vehicle Num": string;
	"Test Stage"?: string;
	"Test Status": string;
	"Start Date": string;
	"End Date"?: string;
	final_eol_result?: string;
}

@Component({
	selector: "app-retrofit-dashboard-table",
	templateUrl: "./retrofit-dashboard-table.component.html",
	styleUrls: ["./retrofit-dashboard-table.component.css"],
})
export class RetrofitDashboardTableComponent implements OnInit {
	constructor(
		private retrofitService: RetrofitService,
		private dialouge: MatDialog,
	) {}
	@Input() packId: string;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(FinalPdfReportComponent)
	pdfReportContainer: FinalPdfReportComponent;

	tableHeaders: string[] = [
		"S.No",
		"Test ID",
		"Vehicle Num",
		"Test Stage",
		"Test Status",
		"Start Date",
		"End Date",
		"Analytics",
		"Downloads",
	];

	tableIcons: {
		pt: string;
		ft: string;
		csv: string;
	} = {
		pt: "assets/img/epack-dashboard/PT.png",
		ft: "assets/img/epack-dashboard/FT.png",
		csv: "assets/img/epack-dashboard/CSV.png",
	};

	tableData: MatTableDataSource<EolTableData> =
		new MatTableDataSource<EolTableData>([]);

	private masterEolData: DashboardDataRes[] = [];

	ngOnInit(): void {
		this.getEpackEolDataByTestRig();
	}

	onShowMetaData(testId: string) {
		this.retrofitService.getTestMetaData(testId).subscribe({
			next: (res) => {
				if (res.body.success) {
					const { metaData, testInfo } = res.body.data;
					const failedCases: Record<string, Record<string, string>> = {
						stage8: { "Fault check": "1" },
					};
					const {
						master_pack_id,
						motor_controller_number,
						motor_serial_number,
						pkn_id,
						slave_pack_id,
						test_id,
						user_id,
						vehicle_number,
					} = testInfo;

					this.dialouge.open(MetadataComponent, {
						height: "95%",
						width: "90%",
						maxWidth: "1250px",
						data: {
							testId: testId,
							failedCases: failedCases,
							metaData,
							testInfo: {
								"Vehicle Number": vehicle_number,
								"Test Id": test_id,
								"Vehicle S.N": motor_serial_number,
								"User Id": user_id,
								"Master Pack Id": master_pack_id,
								"PKN ID": pkn_id,
								"Slave Pack Id": slave_pack_id,
								"Motor Controller Number": motor_controller_number,
							},
						},
					});
				}
			},
		});

		// this.openFallbackDialouge(testId);
	}

	getEpackEolDataByTestRig() {
		this.retrofitService.getEpackEolDataByTestRig().subscribe({
			next: (res) => {
				if (res.body.success) {
					this.masterEolData = res.body.data;
					this.setTableData(this.masterEolData);
				}
			},
			error: (error) => {
				console.log(error);
			},
		});
	}

	setTableData(data: DashboardDataRes[]) {
		const NA = "N/A";
		const resData: EolTableData[] = data.map((data, index) => {
			const {
				metaData: { preload, onroad },
				test_id,
				test_stage,
				test_status,
			} = data;

			const tableData: EolTableData = {
				"S.No": index + 1,
				"Test ID": test_id,
				"Vehicle Num": preload?.vehicle_number ?? NA,
				"Test Stage": test_stage ?? NA,
				"Test Status": test_status ?? NA,
				"Start Date": moment(preload?.test_date).format("DD-MM-YYYY") ?? NA,
				"End Date": onroad?.test_date
					? moment(onroad.test_date).format("DD-MM-YYYY")
					: NA,
			};

			return tableData;
		});

		if (this.packId) {
			this.tableData = new MatTableDataSource<EolTableData>(
				resData
					.filter((_) => _["Battery ID"] === this.packId)
					.map((_, i) => ({ ..._, "S.No": i + 1 })),
			);
		} else {
			this.tableData = new MatTableDataSource<EolTableData>(resData);
		}

		this.tableData.paginator = this.paginator;
	}

	applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.tableData.filter = filterValue.trim().toLowerCase();

		if (this.tableData.paginator) {
			this.tableData.paginator.firstPage();
		}
	}

	openFallbackDialouge(testId: string) {
		Swal.fire({
			title: "Please wait...",
			text: "Feature is being enabed soon",
			icon: "info",
		});
	}

	downloadResource(testId: string) {
		const { metaData } = this.masterEolData.find((_) => _.test_id === testId);

		const fileByTestType: Record<ExtendedRetrofitTestType, string[]> = {
			"pre-load": metaData?.preload?.file_name
				? [metaData?.preload?.file_name]
				: [],
			"post-load": metaData?.postload?.file_name
				? [metaData?.postload?.file_name]
				: [],
			"fast-charge": metaData?.fastchargeCsv?.map((_) => _.file_name) ?? [],
			"fast-charge-pdf": metaData?.fastchargePdf?.file_name
				? [metaData?.fastchargePdf?.file_name]
				: [],
			"on-road": metaData?.onroadCsv?.map((_) => _.file_name) ?? [],
			"on-road-pdf": metaData?.onroadPdf?.file_name
				? [metaData?.onroadPdf?.file_name]
				: [],
		};

		this.dialouge.open(ResourcesDownloadComponent, {
			data: {
				testId: testId,
				fileByTestType: fileByTestType,
			},
			// height: "90%",
			maxHeight: "600px",
			width: "50%",
			maxWidth: "500px",
		});
	}

	generateFullTestReport(testId: string) {
		this.pdfReportContainer.generatePdfDocument(testId);
	}

	getMetadataReport() {
		Swal.fire({
			title: "Select Test Type",
			text: "Please select one of the following options:",
			icon: "question",
			showConfirmButton: false,
			html: `
				<div style="display: flex; flex-direction: column; gap: 10px; margin-top: 2rem">
					<button id="pre-load" class="btn btn-primary">Pre-Load</button>
					<button id="post-load" class="btn btn-primary">Post-Load</button>
					<button id="fast-charge" class="btn btn-primary">Fast-Charge</button>
					<button id="on-road" class="btn btn-primary">On-Road</button>
				</div>`,
			didOpen: () => {
				const preLoadBtn = document.getElementById("pre-load");
				const postLoadBtn = document.getElementById("post-load");
				const fastChargeBtn = document.getElementById("fast-charge");
				const onRoadBtn = document.getElementById("on-road");

				preLoadBtn?.addEventListener("click", () => {
					Swal.close({ value: "pre-load" } as any);
				});
				postLoadBtn?.addEventListener("click", () => {
					Swal.close({ value: "post-load" } as any);
				});
				fastChargeBtn?.addEventListener("click", () => {
					Swal.close({ value: "fast-charge" } as any);
				});
				onRoadBtn?.addEventListener("click", () => {
					Swal.close({ value: "on-road" } as any);
				});
			},
		}).then((res) => {
			const test_type = res.value as RetrofitTestType;

			if (test_type) {
				this.retrofitService.getMetadataReport(test_type).subscribe({
					next: (res) => {
						if (res.body.success) {
							const csvColumns: string[] = [];
							const columns = new Set();

							const csvData: Array<Record<string, unknown>> = [];

							res.body.data?.forEach((data: Record<string, unknown>) => {
								Object.keys(data).forEach((_) => {
									if (!columns.has(_)) {
										columns.add(_);
										csvColumns.push(_);
									}
								});
							});

							for (let i = 0; i < res.body?.data?.length; i++) {
								const data = res.body.data[i];
								const result: Record<string, unknown> = {};
								const currentDataKeys = Object.keys(data);

								csvColumns.forEach((key) => {
									if (!currentDataKeys.includes(key)) {
										result[key] = "N/A";
									} else {
										result[key] = data[key] !== null ? data[key] : "N/A";
									}
								});

								csvData.push(result);
							}

							const exportOptions: Options = {
								useKeysAsHeaders: true,
								useTextFile: false,
								filename: `rf_${test_type}_metadata_${moment(new Date()).format(
									"YYYY-MM-DD",
								)}`,
							};

							const csvExporter = new ExportToCsv(exportOptions);
							csvExporter.generateCsv(csvData);
						}
					},
					error: (err) => {
						console.log("Error getting csv report meta data: ");
					},
				});
			}
		});
	}
}
